/**
 * Nasomin cart guard — runs on every page.
 *
 * Removes a free-gift line item from the cart when its paired paid
 * sibling has been removed. Pairing is tracked via the line item
 * properties `_bundle_group_id` and `_bundle_role` (parent / free)
 * written when the Nasomin Bundle widget adds items.
 *
 * Also removes the two known Nasomine gift variants (57406005477758,
 * 57411954377086) if they appear in the cart on their own without a
 * `_bundle_group_id`, so the free gift never lives alone.
 */
(function () {
  'use strict';

  const GIFT_VARIANT_IDS = new Set([
    57406005477758,
    57411954377086,
  ]);

  let validating = false;
  let observed = false;

  async function validateCart() {
    if (validating) return;
    validating = true;
    try {
      const cart = await fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
      if (!cart || !Array.isArray(cart.items)) return;

      const lines = cart.items;
      const changes = [];

      const groups = {};
      lines.forEach(function (item, idx) {
        const props = item.properties || {};
        const gid = props._bundle_group_id;
        if (!gid) return;
        if (!groups[gid]) groups[gid] = { parents: [], free: [] };
        if (props._bundle_role === 'parent') {
          groups[gid].parents.push({ item: item, line: idx + 1 });
        } else if (props._bundle_role === 'free') {
          groups[gid].free.push({ item: item, line: idx + 1 });
        }
      });

      Object.keys(groups).forEach(function (gid) {
        const g = groups[gid];
        const parentCount = g.parents.reduce(function (n, p) { return n + p.item.quantity; }, 0);
        if (g.free.length > 0 && parentCount < 1) {
          g.free.forEach(function (c) { changes.push({ line: c.line, quantity: 0 }); });
        }
      });

      lines.forEach(function (item, idx) {
        if (!GIFT_VARIANT_IDS.has(Number(item.variant_id))) return;
        const props = item.properties || {};
        if (!props._bundle_group_id) {
          changes.push({ line: idx + 1, quantity: 0 });
        }
      });

      if (!changes.length) return;

      const updates = {};
      changes.forEach(function (c) {
        const item = lines[c.line - 1];
        if (item) updates[item.key] = c.quantity;
      });
      if (!Object.keys(updates).length) return;

      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ updates: updates }),
      });
      if (res.ok) await refreshCartUI();
    } catch (e) {
      console.warn('[nasomin-cart-guard] validation failed', e);
    } finally {
      validating = false;
    }
  }

  async function refreshCartUI() {
    try {
      const res = await fetch('/?sections=cart-drawer,cart-icon-bubble', { credentials: 'same-origin' });
      if (!res.ok) return;
      const data = await res.json();
      replaceSection('CartDrawer', data['cart-drawer'], '#CartDrawer');
      replaceSection('cart-icon-bubble', data['cart-icon-bubble'], '#cart-icon-bubble');
    } catch (e) {
      // ignore
    }
  }

  function replaceSection(targetId, html, selector) {
    if (!html) return;
    const target = document.getElementById(targetId);
    if (!target) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const src = parsed.querySelector(selector) || parsed.querySelector('#' + targetId);
    if (src) target.innerHTML = src.innerHTML;
  }

  function observeCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || observed) return;
    observed = true;
    let t;
    const obs = new MutationObserver(function () {
      if (t) clearTimeout(t);
      t = setTimeout(validateCart, 300);
    });
    obs.observe(drawer, { childList: true, subtree: true });
  }

  function init() {
    validateCart();
    observeCartDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
